import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { AliasBadge, StatusBadge } from './Badges';
import { Search, Building2, Users, Receipt, DollarSign, AlertTriangle, Target } from 'lucide-react';

// =============================================================================
// AdminOpsOverview — top-line marketplace/revenue/payouts/quality tiles
// =============================================================================
export function AdminOpsOverview() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/ops/summary');
        setSummary(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-center py-16 text-gray-500 text-sm font-mono">Loading operating snapshot...</div>;
  if (!summary) return <div className="text-center py-16 text-red-600 text-sm">Failed to load summary</div>;

  const m = summary.marketplace;
  const r = summary.revenue;
  const p = summary.payouts.seller_earnings_by_status || {};
  const q = summary.quality;

  return (
    <div className="space-y-6" data-testid="admin-ops-overview">
      <SectionHeader icon={Building2} title="Marketplace">
        <div className="text-xs text-gray-500 font-mono">Snapshot at {new Date(summary.generated_at).toLocaleString()}</div>
      </SectionHeader>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden">
        <Tile label="Active buyers" value={m.active_buyers} icon={Users} />
        <Tile label="Active sellers" value={m.active_sellers} icon={Users} />
        <Tile label="Live listings" value={m.live_listings} icon={Receipt} />
        <Tile label="Active demand" value={m.active_demand_profiles} icon={Target} />
        <Tile label="Matches" value={m.matches} icon={Receipt} />
        <Tile label="Sold leads" value={m.sold_leads_total} icon={Receipt} />
      </div>

      <SectionHeader icon={DollarSign} title="Revenue" />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden">
        <Tile label="MRR (membership)" value={`$${r.mrr.toFixed(0)}`} sub={`${r.active_memberships} paying`} />
        <Tile label="GMV (all-time)" value={`$${r.gmv_all_time.toFixed(2)}`} sub={`${r.paid_transactions_all_time} txns`} />
        <Tile label="Platform fees (all-time)" value={`$${r.platform_fees_all_time.toFixed(2)}`} sub="20% take-rate" />
        <Tile label="GMV (this month)" value={`$${r.gmv_this_month.toFixed(2)}`} sub={`fees $${r.platform_fees_this_month.toFixed(2)}`} />
        <Tile label="Refunds" value={r.refunds} />
      </div>

      <SectionHeader icon={DollarSign} title="Seller payouts" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden">
        {['pending', 'approved', 'paid', 'cancelled'].map(status => {
          const row = p[status] || { total: 0, count: 0 };
          return (
            <Tile key={status} label={`${status}`} value={`$${row.total.toFixed(2)}`} sub={`${row.count} row${row.count === 1 ? '' : 's'}`} />
          );
        })}
      </div>

      <SectionHeader icon={AlertTriangle} title="Quality" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden max-w-xs">
        <Tile label="Open disputes" value={q.open_disputes} />
      </div>
    </div>
  );
}

// =============================================================================
// AdminTransactionInspector — search by transaction_id → full 360° view
// =============================================================================
export function AdminTransactionInspector() {
  const [txnId, setTxnId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pauseTarget, setPauseTarget] = useState(null); // {user_id, reason}

  const inspect = async (id) => {
    const useId = (id || txnId).trim();
    if (!useId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/ops/transaction/${useId}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transaction not found');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const pauseUser = async () => {
    if (!pauseTarget?.user_id || !pauseTarget?.reason?.trim()) {
      toast.error('Reason required');
      return;
    }
    try {
      await api.post(`/admin/ops/users/${pauseTarget.user_id}/pause`, { reason: pauseTarget.reason });
      toast.success('User paused');
      setPauseTarget(null);
      inspect();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pause failed');
    }
  };

  const resumeUser = async (userId) => {
    if (!window.confirm('Resume this user?')) return;
    try {
      await api.post(`/admin/ops/users/${userId}/resume`);
      toast.success('User resumed');
      inspect();
    } catch (e) {
      toast.error('Resume failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-inspector">
      <div className="flex gap-2">
        <Input
          value={txnId}
          onChange={e => setTxnId(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && inspect()}
          placeholder="Enter transaction_id (e.g. txn_abc123)"
          data-testid="inspector-txn-input"
        />
        <Button onClick={() => inspect()} disabled={loading || !txnId.trim()} data-testid="inspector-search-btn">
          <Search className="w-4 h-4 mr-1.5" /> {loading ? 'Loading…' : 'Inspect'}
        </Button>
      </div>

      {!data && !loading && (
        <div className="border border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500 text-sm">
          Enter a transaction ID to see the full trail: lead, buyer identity, seller identity, seller earning, dispute, outcome and webhook history.
        </div>
      )}

      {data && (
        <div className="space-y-4" data-testid="inspector-result">
          {/* Header */}
          <div className="border border-gray-200 rounded-lg p-5">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <div className="font-mono text-xs text-gray-500">{data.transaction?.transaction_id}</div>
                <div className="font-heading text-2xl font-semibold text-gray-900 mt-1">
                  ${data.transaction?.amount?.toFixed(2)} — {data.transaction?.payment_status}
                </div>
                {data.transaction?.stripe_session_id && (
                  <div className="text-xs text-gray-500 font-mono mt-1">Stripe: {data.transaction.stripe_session_id.slice(0, 30)}…</div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                <StatusBadge status={data.transaction?.payment_status} />
                {data.transaction?.paid_at && <span className="text-xs text-gray-500 font-mono">{new Date(data.transaction.paid_at).toLocaleString()}</span>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <IdentityCard label="Buyer (real)" user={data.buyer} onPause={(uid) => setPauseTarget({ user_id: uid, reason: '' })} onResume={resumeUser} />
              <IdentityCard label="Seller (real)" user={data.seller} onPause={(uid) => setPauseTarget({ user_id: uid, reason: '' })} onResume={resumeUser} />
            </div>
          </div>

          {/* Lead + Fee */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Lead">
              {data.lead ? (
                <div className="text-sm space-y-1">
                  <Row k="Lead ID" v={<span className="font-mono">{data.lead.lead_id}</span>} />
                  <Row k="Category" v={data.lead.category} />
                  <Row k="Region" v={data.lead.region} />
                  <Row k="Digital title" v={<span className="font-mono text-xs">{data.lead.digital_title}</span>} />
                  <Row k="Current status" v={<StatusBadge status={data.lead.status} />} />
                </div>
              ) : <div className="text-sm text-gray-500 italic">Lead deleted or missing</div>}
            </Panel>

            <Panel title="Seller earning">
              {data.seller_earning ? (
                <div className="text-sm space-y-1">
                  <Row k="Gross" v={`$${data.seller_earning.gross_sale?.toFixed(2)}`} />
                  <Row k="Platform fee (20%)" v={`$${data.seller_earning.platform_fee?.toFixed(2)}`} />
                  <Row k="Seller net" v={<span className="font-semibold text-emerald-700">${data.seller_earning.net_earning?.toFixed(2)}</span>} />
                  <Row k="Payout status" v={<StatusBadge status={data.seller_earning.payout_status} />} />
                </div>
              ) : <div className="text-sm text-gray-500 italic">No seller earning row</div>}
            </Panel>
          </div>

          {/* Outcome */}
          {data.outcome && (
            <Panel title="Outcome Ledger">
              <div className="text-sm space-y-1">
                <Row k="Stage" v={<span className="font-mono">{data.outcome.stage}</span>} />
                {data.outcome.deal_value && <Row k="Deal value" v={`$${data.outcome.deal_value.toFixed(2)}`} />}
                {data.outcome.appointment_at && <Row k="Appointment" v={new Date(data.outcome.appointment_at).toLocaleString()} />}
                {data.outcome.qualification_notes && <Row k="Notes" v={data.outcome.qualification_notes} />}
              </div>
            </Panel>
          )}

          {/* Dispute */}
          {data.dispute && (
            <Panel title="Dispute" tone="danger">
              <div className="text-sm space-y-1">
                <Row k="Reason" v={data.dispute.reason} />
                <Row k="Status" v={<StatusBadge status={data.dispute.status} />} />
                <Row k="Description" v={data.dispute.description} />
              </div>
            </Panel>
          )}

          {/* Webhooks */}
          <Panel title={`Stripe webhook events (${data.stripe_webhooks?.length || 0})`}>
            {data.stripe_webhooks?.length ? (
              <div className="text-xs font-mono space-y-1">
                {data.stripe_webhooks.map(w => (
                  <div key={w.event_id} className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${w.processed ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                    <span className="text-gray-500">{new Date(w.received_at).toLocaleString()}</span>
                    <span className="font-semibold">{w.event_type}</span>
                    <span className="text-gray-400 truncate">{w.event_id}</span>
                  </div>
                ))}
              </div>
            ) : <div className="text-sm text-gray-500 italic">No webhook events for this session</div>}
          </Panel>

          {/* Ledger events */}
          <Panel title={`Ledger events (${data.ledger_events?.length || 0})`}>
            {data.ledger_events?.length ? (
              <div className="text-xs font-mono space-y-1">
                {data.ledger_events.map(l => (
                  <div key={l.entry_id} className="flex items-center gap-2">
                    <span className="text-gray-500">{new Date(l.recorded_at || l.created_at).toLocaleString()}</span>
                    <span className="font-semibold">{l.event_type}</span>
                    {l.amount_cents != null && <span className="text-emerald-700">${(l.amount_cents / 100).toFixed(2)}</span>}
                  </div>
                ))}
              </div>
            ) : <div className="text-sm text-gray-500 italic">No ledger events</div>}
          </Panel>
        </div>
      )}

      {/* Pause user dialog */}
      {pauseTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPauseTarget(null)}>
          <div className="bg-white rounded-lg p-5 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="font-heading text-lg font-semibold mb-2">Pause user</div>
            <div className="text-xs font-mono text-gray-500 mb-3">{pauseTarget.user_id}</div>
            <Textarea
              placeholder="Reason for pausing (required)"
              value={pauseTarget.reason}
              onChange={e => setPauseTarget(p => ({ ...p, reason: e.target.value }))}
              data-testid="pause-reason-input"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setPauseTarget(null)}>Cancel</Button>
              <Button onClick={pauseUser} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-pause-btn">Pause</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SectionHeader = ({ icon: Icon, title, children }) => (
  <div className="flex items-center justify-between border-b border-gray-200 pb-2">
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-gray-500" />
      <div className="text-xs uppercase tracking-[0.2em] text-gray-700 font-mono font-semibold">{title}</div>
    </div>
    {children}
  </div>
);

const Tile = ({ label, value, sub, icon: Icon }) => (
  <div className="bg-white p-4">
    <div className="flex items-center gap-1.5 mb-1">
      {Icon && <Icon className="w-3.5 h-3.5 text-gray-400" />}
      <span className="text-[10px] uppercase tracking-wider text-gray-500 font-mono">{label}</span>
    </div>
    <div className="font-mono text-xl font-semibold text-gray-900">{value}</div>
    {sub && <div className="text-[10px] text-gray-400 font-mono mt-0.5">{sub}</div>}
  </div>
);

const Panel = ({ title, tone, children }) => (
  <div className={`border rounded-lg p-4 ${tone === 'danger' ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
    <div className="text-xs uppercase tracking-wider font-mono text-gray-600 font-semibold mb-2">{title}</div>
    {children}
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex items-start gap-2">
    <div className="text-gray-500 min-w-[130px]">{k}</div>
    <div className="text-gray-900">{v}</div>
  </div>
);

const IdentityCard = ({ label, user, onPause, onResume }) => (
  <div className="border border-gray-200 rounded p-3">
    <div className="flex items-center justify-between mb-1">
      <div className="text-xs uppercase tracking-wider text-gray-500 font-mono">{label}</div>
      {user && <AliasBadge alias={user.alias} clickable={true} userId={user.user_id} />}
    </div>
    {user ? (
      <div className="space-y-0.5 text-sm">
        <div className="font-semibold text-gray-900">{user.name}</div>
        <div className="text-gray-700">{user.company_name || '—'}</div>
        <div className="text-xs text-gray-500 font-mono">{user.email}</div>
        {user.abn && <div className="text-xs text-gray-500 font-mono">ABN {user.abn}</div>}
        <div className="flex items-center gap-2 mt-2">
          <StatusBadge status={user.status || 'active'} />
          {user.status === 'paused' ? (
            <Button size="sm" variant="outline" className="text-xs h-6" onClick={() => onResume(user.user_id)} data-testid={`resume-${user.user_id}`}>Resume</Button>
          ) : (
            <Button size="sm" variant="outline" className="text-xs h-6 text-red-700 border-red-200" onClick={() => onPause(user.user_id)} data-testid={`pause-${user.user_id}`}>Pause</Button>
          )}
        </div>
        {user.paused_reason && <div className="text-xs text-red-700 italic mt-1">Paused: {user.paused_reason}</div>}
      </div>
    ) : <div className="text-sm text-gray-500 italic">Missing</div>}
  </div>
);
